import type { DecodeHormoneReference } from "../../../types/decode";

const HORMONE_DISPLAY_NAMES: Record<string, string> = {
  fsh: "FSH",
  lh: "LH",
  estradiol: "Estradiol",
  progesterone: "Progesterone",
  amh: "AMH",
  prolactin: "Prolactin",
  tsh: "TSH",
};

interface HormoneCardProps {
  hormone: DecodeHormoneReference;
  explanation: string;
  explanationError?: string;
  explanationTouched?: boolean;
  onExplanationChange: (value: string) => void;
  onExplanationBlur: () => void;
}

export function HormoneCard({
  hormone,
  explanation,
  explanationError,
  explanationTouched,
  onExplanationChange,
  onExplanationBlur,
}: Readonly<HormoneCardProps>) {
  const { reference_ranges } = hormone;
  const displayName = HORMONE_DISPLAY_NAMES[hormone.hormone] ?? hormone.hormone.toUpperCase();

  const ranges = [
    { label: "Low Range", data: reference_ranges.low },
    { label: "Normal Range", data: reference_ranges.normal },
    { label: "High Range", data: reference_ranges.high },
  ];

  return (
    <div data-testid={`hormone-card-${hormone.hormone}`}>
      <h3 className="text-base font-medium text-text-primary mb-3">{displayName} Hormone</h3>

      <div className="grid grid-cols-3 gap-4 mb-4">
        {ranges.map((range) => (
          <div key={range.label}>
            <span className="text-sm font-medium text-brand-brown block mb-1">
              {range.label}
            </span>
            <div className="flex items-baseline gap-1 text-sm text-text-primary">
              <span className="text-text-secondary">From</span>
              <span className="font-semibold">{range.data.from}</span>
              <span className="text-text-secondary ml-2">To</span>
              <span className="font-semibold">{range.data.to}</span>
              <span className="text-text-secondary ml-1">{range.data.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label
          htmlFor={`explanation-${hormone.hormone}`}
          className="text-sm font-medium text-text-primary block mb-2"
        >
          Universal {displayName} Explanation
        </label>
        <textarea
          id={`explanation-${hormone.hormone}`}
          className={`w-full border rounded-2xl p-4 text-sm leading-relaxed text-text-primary bg-bg-primary resize-none focus:outline-none min-h-[53px] ${
            explanationTouched && explanationError
              ? "border-input-stroke-error"
              : "border-stroke-primary focus:border-brand-brown"
          }`}
          value={explanation}
          onChange={(e) => onExplanationChange(e.target.value)}
          onBlur={onExplanationBlur}
          placeholder={`Enter universal ${displayName} explanation...`}
        />
        {explanationTouched && explanationError && (
          <p className="text-input-hint-error text-sm mt-1">{explanationError}</p>
        )}
      </div>
    </div>
  );
}
